import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Package, Plus, TrendingUp, TrendingDown, AlertCircle, FileText, ArrowLeft, Settings, Filter as FilterIcon, PackagePlus, PackageMinus, AlertTriangle, History, ArrowDown, Shirt } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useToast } from '@/components/ui/use-toast';
import DataTable from '@/components/DataTable';
import Pagination from '@/components/Pagination';
import { useAuth } from '@/contexts/AuthContext';
import RegistrarEntradaInsumosModal from '@/components/inventario/RegistrarEntradaInsumosModal';
import AjustarInsumoModal from '@/components/inventario/AjustarInsumoModal';
import SeleccionarInsumoAjusteModal from '@/components/inventario/SeleccionarInsumoAjusteModal';
import ExportExcelButton from '@/components/ExportExcelButton';
import FilterDrawer from '@/components/FilterDrawer';
import AppliedFilters from '@/components/AppliedFilters';
import { fetchInventarioInsumos, agregarMultiplesInsumos } from './services/inventario-insumos.services';
import { fetchMovimientosInsumos } from '@/services/movimientosInsumos.service';
import { fetchEntregas } from '@/services/entregasInsumos.service';

const ITEMS_PER_PAGE = 10;

const InventarioInsumosPage = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('inventario');
  
  // Estados para Inventario Actual
  const [inventario, setInventario] = useState([]);
  const [isLoadingInventario, setIsLoadingInventario] = useState(false);
  const [currentPageInventario, setCurrentPageInventario] = useState(1);
  const [totalItemsInventario, setTotalItemsInventario] = useState(0);
  
  // Estados para Movimientos (placeholder - implementar según backend)
  const [movimientos, setMovimientos] = useState([]);
  const [isLoadingMovimientos, setIsLoadingMovimientos] = useState(false);
  const [currentPageMovimientos, setCurrentPageMovimientos] = useState(1);
  const [totalItemsMovimientos, setTotalItemsMovimientos] = useState(0);
  
  // Estados para Historial Detallado
  const [historialDetallado, setHistorialDetallado] = useState([]);
  const [isLoadingHistorial, setIsLoadingHistorial] = useState(false);
  const [currentPageHistorial, setCurrentPageHistorial] = useState(1);
  const [totalHistorial, setTotalHistorial] = useState(0);
  
  // Estados para Modales
  const [isEntradaModalOpen, setIsEntradaModalOpen] = useState(false);
  const [isAjusteModalOpen, setIsAjusteModalOpen] = useState(false);
  const [selectedInsumoAjuste, setSelectedInsumoAjuste] = useState(null);
  const [isAjusteGeneralModalOpen, setIsAjusteGeneralModalOpen] = useState(false);
  
  // Estados para Filtros
  const [isFilterDrawerOpen, setIsFilterDrawerOpen] = useState(false);
  const [filters, setFilters] = useState({});
  
  // Estados para estadísticas
  const [stats, setStats] = useState({
    totalInsumos: 0,
    totalCantidad: 0,
    bajoStock: 0,
    agotados: 0
  });

  // Columnas para tabla de Inventario Actual
  const columnsInventario = [
    {
      id: 'consumible',
      label: 'Insumo',
      sortable: true,
      render: (row) => (
        <div className="flex items-center gap-2">
          <Package className="h-4 w-4 text-green-600" />
          <span className="font-medium">{row.consumible?.nombre || row.consumible || '-'}</span>
        </div>
      )
    },
    {
      id: 'cantidad_disponible',
      label: 'Cantidad Disponible',
      sortable: true,
      render: (row) => (
        <Badge variant="outline" className="bg-green-50 text-green-700 font-semibold">
          {row.cantidad_disponible}
        </Badge>
      )
    },
    {
      id: 'cantidad_minima',
      label: 'Cantidad Mínima',
      sortable: true,
      render: (row) => (
        <span className="text-sm text-gray-600">{row.cantidad_minima}</span>
      )
    },
    {
      id: 'ubicacion',
      label: 'Ubicación',
      render: (row) => row.ubicacion || '-'
    },
    {
      id: 'estado',
      label: 'Estado',
      sortable: true,
      render: (row) => {
        const colors = {
          'disponible': 'bg-green-100 text-green-800',
          'bajo_stock': 'bg-yellow-100 text-yellow-800',
          'agotado': 'bg-red-100 text-red-800',
          'inactivo': 'bg-gray-100 text-gray-800'
        };
        return (
          <Badge className={colors[row.estado] || 'bg-gray-100 text-gray-800'}>
            {row.estado}
          </Badge>
        );
      }
    },
    {
      id: 'ultima_entrada',
      label: 'Última Entrada',
      render: (row) => row.ultima_entrada 
        ? new Date(row.ultima_entrada).toLocaleDateString('es-ES')
        : '-'
    }
  ];

  // Columnas para tabla de Movimientos
  const columnsMovimientos = [
    {
      id: 'fecha_movimiento',
      label: 'Fecha',
      sortable: true,
      render: (row) => new Date(row.fecha_movimiento).toLocaleString('es-ES', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      })
    },
    {
      id: 'tipo_movimiento',
      label: 'Tipo',
      sortable: true,
      render: (row) => {
        const colors = {
          'entrada': 'bg-green-100 text-green-800',
          'salida': 'bg-red-100 text-red-800',
          'traslado': 'bg-blue-100 text-blue-800',
          'ajuste': 'bg-yellow-100 text-yellow-800',
          'baja': 'bg-gray-100 text-gray-800'
        };
        const icons = {
          'entrada': <TrendingUp className="h-3 w-3" />,
          'salida': <TrendingDown className="h-3 w-3" />,
          'traslado': <Package className="h-3 w-3" />,
          'ajuste': <AlertCircle className="h-3 w-3" />,
          'baja': <FileText className="h-3 w-3" />
        };
        return (
          <Badge className={`${colors[row.tipo_movimiento]} flex items-center gap-1 w-fit`}>
            {icons[row.tipo_movimiento]}
            {row.tipo_movimiento}
          </Badge>
        );
      }
    },
    {
      id: 'consumible',
      label: 'Insumo',
      sortable: true,
      render: (row) => (
        <div className="flex items-center gap-2">
          <Package className="h-4 w-4 text-green-600" />
          <span>{row.consumible?.nombre || '-'}</span>
        </div>
      )
    },
    {
      id: 'cantidad',
      label: 'Cantidad',
      sortable: true,
      render: (row) => {
        const cantidad = row.cantidad;
        const isNegative = cantidad < 0;
        return (
          <Badge variant="outline" className="font-semibold">
            {isNegative ? '' : '+'}{cantidad}
          </Badge>
        );
      }
    },
    {
      id: 'numero_factura',
      label: 'Factura/Consecutivo',
      render: (row) => row.numero_factura || row.consecutivo || '-'
    },
    {
      id: 'usuario',
      label: 'Usuario',
      render: (row) => row.usuario?.nombre || 'N/A'
    },
    {
      id: 'cantidad_despues',
      label: 'Stock Resultante',
      render: (row) => (
        <span className="text-sm font-medium text-gray-700">
          {row.cantidad_despues !== undefined ? row.cantidad_despues : '-'}
        </span>
      )
    }
  ];

  // Fetch Inventario Actual
  const loadInventario = useCallback(async () => {
    setIsLoadingInventario(true);
    try {
      const offset = (currentPageInventario - 1) * ITEMS_PER_PAGE;
      const response = await fetchInventarioInsumos({
        page: currentPageInventario,
        limit: ITEMS_PER_PAGE,
        offset,
        query: filters
      });

      setInventario(response.data || []);
      setTotalItemsInventario(response.totalRecords || 0);

      // Calcular estadísticas
      const total = response.data?.length || 0;
      const totalCantidad = response.data?.reduce((sum, item) => sum + item.cantidad_disponible, 0) || 0;
      const bajoStock = response.data?.filter(item => item.estado === 'bajo_stock').length || 0;
      const agotados = response.data?.filter(item => item.estado === 'agotado').length || 0;

      setStats({
        totalInsumos: response.totalRecords || 0,
        totalCantidad,
        bajoStock,
        agotados
      });
    } catch (error) {
      console.error('Error al cargar inventario:', error);
      toast({
        title: 'Error',
        description: 'No se pudo cargar el inventario',
        variant: 'destructive'
      });
    } finally {
      setIsLoadingInventario(false);
    }
  }, [currentPageInventario, filters, toast]);

  // Fetch Movimientos
  const loadMovimientos = useCallback(async () => {
    setIsLoadingMovimientos(true);
    try {
      const offset = (currentPageMovimientos - 1) * ITEMS_PER_PAGE;
      const response = await fetchMovimientosInsumos({
        page: currentPageMovimientos,
        limit: ITEMS_PER_PAGE,
        offset,
        ...filters
      });

      if (response && response.movimientos) {
        setMovimientos(response.movimientos);
        setTotalItemsMovimientos(response.total || 0);
      } else {
        setMovimientos([]);
        setTotalItemsMovimientos(0);
      }
    } catch (error) {
      console.error('Error al cargar movimientos:', error);
      toast({
        title: 'Error',
        description: 'No se pudo cargar los movimientos',
        variant: 'destructive'
      });
    } finally {
      setIsLoadingMovimientos(false);
    }
  }, [currentPageMovimientos, filters, toast]);

  // Fetch Historial Detallado
  const loadHistorialDetallado = useCallback(async () => {
    setIsLoadingHistorial(true);
    try {
      const params = {
        limit: 1000, // Obtener todos los registros para la matriz
        estadoExcluir: 'pendiente' // Excluir entregas pendientes
      };
      
      // Agregar filtros de fecha si existen
      if (filters.fechaInicio) {
        params.fechaInicio = filters.fechaInicio;
      }
      if (filters.fechaFin) {
        params.fechaFin = filters.fechaFin;
      }
            
      // Obtener TODO el inventario (sin paginación) para hacer el match correcto
      const inventarioResponse = await fetchInventarioInsumos({
        limit: 10000,
        offset: 0,
        query: {}
      });
      
      // Guardar inventario completo en el estado
      if (inventarioResponse.data) {
        setInventario(inventarioResponse.data);
      }
      
      // Obtener todas las entregas
      const response = await fetchEntregas(params);
      
      const data = response.data || [];
      
      // Filtrar en el frontend también para asegurar que solo se incluyan entregadas
      const filteredData = data.filter(item => item.estado === 'entregado');
      
      // Ordenar por fecha ascendente (más antiguas primero para mostrar cronológicamente)
      const sortedData = filteredData.sort((a, b) => {
        const dateA = new Date(a.fecha_entrega || a.createdAt);
        const dateB = new Date(b.fecha_entrega || b.createdAt);
        return dateA - dateB;
      });
      

      setHistorialDetallado(sortedData);
      setTotalHistorial(sortedData.length);
    } catch (error) {
      console.error('Error al cargar historial:', error);
      toast({
        title: 'Error',
        description: 'No se pudo cargar el historial detallado',
        variant: 'destructive',
      });
    } finally {
      setIsLoadingHistorial(false);
    }
  }, [filters, toast]);

  // Construir matriz de entregas
  const buildStockMatrix = () => {
    if (!historialDetallado || historialDetallado.length === 0) {
      return { supplies: [], events: [] };
    }

    // Recopilar todos los insumos únicos y buscar su inventario actual
    const suppliesMap = new Map();
    
    
    historialDetallado.forEach(entrega => {
      if (entrega.items && Array.isArray(entrega.items)) {
        entrega.items.forEach(item => {
          if (!suppliesMap.has(item.id)) {
            // Buscar el inventario actual de este insumo
            const inventarioItem = inventario.find(inv => inv.consumible?.id === item.id);
            
            console.log(`Buscando inventario para ${item.nombre} (${item.id}):`, 
              inventarioItem ? `Encontrado: ${inventarioItem.cantidad_disponible}` : 'NO ENCONTRADO');
            
            suppliesMap.set(item.id, {
              id: item.id,
              nombre: item.nombre,
              tipo: item.tipo || 'General',
              inventarioActual: inventarioItem?.cantidad_disponible || 0
            });
          }
        });
      }
    });

    // Crear eventos (entregas)
    const events = historialDetallado.map(entrega => {
      const itemsMap = new Map();
      
      if (entrega.items && Array.isArray(entrega.items)) {
        entrega.items.forEach(item => {
          itemsMap.set(item.id, {
            cantidad: item.cantidad,
            impacto: -item.cantidad // Las entregas restan del inventario
          });
        });
      }

      // Construir el texto de destino: Edificio + Hospedaje (si existe)
      const edificioNombre = entrega.edificio?.nombre || 'N/A';
      const hospedajeNombre = entrega.hospedaje?.nombre || null;
      const destino = hospedajeNombre ? `${edificioNombre} - ${hospedajeNombre}` : edificioNombre;

      return {
        id: entrega._id,
        consecutivo: entrega.solicitud_id || entrega._id,
        fecha: new Date(entrega.fecha_entrega || entrega.createdAt),
        edificio: edificioNombre,
        hospedaje: hospedajeNombre,
        destino: destino,
        itemsMap
      };
    });

    const supplies = Array.from(suppliesMap.values()).sort((a, b) => 
      a.nombre.localeCompare(b.nombre)
    );

    return { supplies, events };
  };

  // Handlers
  const handleEntradaSuccess = () => {
    setIsEntradaModalOpen(false);
    loadInventario();
    loadMovimientos();
    toast({
      title: 'Éxito',
      description: 'Entrada registrada correctamente',
    });
  };

  const handleAjusteSuccess = () => {
    setIsAjusteModalOpen(false);
    setSelectedInsumoAjuste(null);
    loadInventario();
    loadMovimientos();
    toast({
      title: 'Éxito',
      description: 'Ajuste registrado correctamente',
    });
  };

  const handleSelectInsumoAjuste = (insumo) => {
    setSelectedInsumoAjuste(insumo);
    setIsAjusteGeneralModalOpen(false);
    setIsAjusteModalOpen(true);
  };

  const handleAgregarInsumosSuccess = async (insumosData) => {
    try {
      const response = await agregarMultiplesInsumos(insumosData);
      
      if (response && response.state) {
        const { exitosos, total_procesados, total_errores } = response.data;
        
        await loadInventario();
        
        toast({
          title: '✅ Insumos Procesados',
          description: (
            <div className="space-y-2">
              <p className="font-semibold">
                {total_procesados} insumo{total_procesados > 1 ? 's' : ''} procesado{total_procesados > 1 ? 's' : ''} exitosamente
              </p>
              {total_errores > 0 && (
                <p className="text-red-500">
                  {total_errores} error{total_errores > 1 ? 'es' : ''}
                </p>
              )}
              <div className="text-xs mt-2 max-h-32 overflow-y-auto">
                {exitosos.map((item, idx) => (
                  <div key={idx} className="py-1">
                    <span className="font-medium">{item.consumible}:</span>{' '}
                    <span className={item.accion === 'creado' ? 'text-green-600' : 'text-blue-600'}>
                      {item.accion === 'creado' ? '✨ Creado' : '📦 Actualizado'}
                    </span>{' '}
                    (+{item.cantidad_agregada} → Total: {item.cantidad_total})
                  </div>
                ))}
              </div>
            </div>
          ),
          duration: 8000,
        });
        
        setIsAgregarInsumosOpen(false);
        return true;
      }
      return false;
    } catch (error) {
      console.error('Error al agregar insumos:', error);
      toast({
        title: 'Error',
        description: 'No se pudieron agregar los insumos',
        variant: 'destructive'
      });
      return false;
    }
  };

  // Configuración de columnas para exportar
  const exportColumnsInventario = [
    { header: 'Insumo', key: 'consumible.nombre' },
    { header: 'Cantidad Disponible', key: 'cantidad_disponible' },
    { header: 'Cantidad Mínima', key: 'cantidad_minima' },
    { header: 'Ubicación', key: 'ubicacion' },
    { header: 'Estado', key: 'estado' },
    { 
      header: 'Última Entrada', 
      key: 'ultima_entrada',
      transform: (value) => value ? new Date(value).toLocaleDateString('es-ES') : '-'
    }
  ];

  const exportColumnsMovimientos = [
    { 
      header: 'Fecha', 
      key: 'fecha_movimiento',
      transform: (value) => value ? new Date(value).toLocaleString('es-ES') : '-'
    },
    { header: 'Tipo', key: 'tipo_movimiento' },
    { header: 'Insumo', key: 'consumible.nombre' },
    { header: 'Cantidad', key: 'cantidad' },
    { 
      header: 'Factura/Consecutivo', 
      key: 'numero_factura',
      transform: (value, row) => row.numero_factura || row.consecutivo || '-'
    },
    { header: 'Usuario', key: 'usuario.nombre' },
    { header: 'Stock Resultante', key: 'cantidad_despues' }
  ];

  // Configuración de filtros
  const filterFieldsInventario = [
    {
      id: 'consumible.nombre',
      label: 'Buscar por nombre',
      type: 'text'
    },
    {
      id: 'estado',
      label: 'Estado',
      type: 'select',
      options: [
        { value: 'disponible', label: 'Disponible' },
        { value: 'bajo_stock', label: 'Bajo Stock' },
        { value: 'agotado', label: 'Agotado' },
        { value: 'inactivo', label: 'Inactivo' }
      ]
    }
  ];

  const filterFieldsMovimientos = [
    {
      id: 'consumible.nombre',
      label: 'Buscar por insumo',
      type: 'text',
      placeholder: 'Nombre del insumo...'
    },
    {
      id: 'tipo_movimiento',
      label: 'Tipo de Movimiento',
      type: 'select',
      options: [
        { value: 'entrada', label: 'Entrada' },
        { value: 'salida', label: 'Salida' },
        { value: 'ajuste', label: 'Ajuste' },
        { value: 'baja', label: 'Baja' }
      ]
    },
    {
      id: 'fecha_movimiento',
      label: 'Rango de Fechas',
      type: 'daterange'
    }
  ];

  const filterFieldsHistorial = [
    {
      id: 'fechaInicio',
      label: 'Fecha Inicio',
      type: 'date'
    },
    {
      id: 'fechaFin',
      label: 'Fecha Fin',
      type: 'date'
    }
  ];

  const handleApplyFilters = (appliedFilters) => {
    setFilters(appliedFilters);
    setCurrentPageInventario(1);
    setCurrentPageMovimientos(1);
    setCurrentPageHistorial(1);
  };

  const handleRemoveFilter = (filterKey) => {
    const newFilters = { ...filters };
    delete newFilters[filterKey];
    setFilters(newFilters);
    setCurrentPageInventario(1);
    setCurrentPageMovimientos(1);
    setCurrentPageHistorial(1);
  };

  // Wrappers para servicios de exportación
  const exportInventarioService = async ({ query, limit, page }) => {
    const offset = (page - 1) * limit;
    const response = await fetchInventarioInsumos({ ...query, limit, page, offset, query });
    return { data: response.data || [] };
  };

  const exportMovimientosService = async ({ query, limit, page }) => {
    const offset = (page - 1) * limit;
    const response = await fetchMovimientosInsumos({ ...query, limit, page, offset });
    return { data: response.movimientos || [] };
  };

  // Effects
  useEffect(() => {
    loadInventario();
  }, [loadInventario]);

  useEffect(() => {
    if (activeTab === 'movimientos') {
      loadMovimientos();
    }
  }, [activeTab, loadMovimientos]);

  useEffect(() => {
    if (activeTab === 'historial') {
      loadHistorialDetallado();
    }
  }, [activeTab, loadHistorialDetallado]);

  // Limpiar filtros al cambiar de tab
  useEffect(() => {
    setFilters({});
  }, [activeTab]);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      transition={{ duration: 0.5 }}
      className="container mx-auto p-6 space-y-6"
    >
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate('/lavanderia')}
            className="h-10 w-10"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
              <Package className="h-8 w-8 text-green-600" />
              Inventario de Insumos
            </h1>
            <p className="text-gray-600 mt-1">
              Gestión de stock de insumos y consumibles
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {user?.role === 'admin' && (
            <Button
              onClick={() => setIsAjusteGeneralModalOpen(true)}
              variant="outline"
              className="flex items-center gap-2 text-orange-600 hover:text-orange-700 hover:bg-orange-50 border-orange-300"
            >
              <Settings className="h-4 w-4" />
              Ajustar/Dar de Baja
            </Button>
          )}
          <Button
            onClick={() => setIsEntradaModalOpen(true)}
            className="flex items-center gap-2 bg-green-600 hover:bg-green-700"
          >
            <Plus className="h-4 w-4" />
            Registrar Entrada
          </Button>
        </div>
      </div>

      {/* Estadísticas */}
      {/* <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Total Insumos</p>
                <p className="text-2xl font-bold text-green-600">{stats.totalInsumos}</p>
              </div>
              <Package className="h-8 w-8 text-green-500" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Cantidad Total</p>
                <p className="text-2xl font-bold text-blue-600">{stats.totalCantidad}</p>
              </div>
              <PackagePlus className="h-8 w-8 text-blue-500" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Bajo Stock</p>
                <p className="text-2xl font-bold text-yellow-600">{stats.bajoStock}</p>
              </div>
              <AlertTriangle className="h-8 w-8 text-yellow-500" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Agotados</p>
                <p className="text-2xl font-bold text-red-600">{stats.agotados}</p>
              </div>
              <PackageMinus className="h-8 w-8 text-red-500" />
            </div>
          </CardContent>
        </Card>
      </div> */}

      {/* Tabs */}
      <Card>
        <CardHeader>
          <CardTitle>Gestión de Inventario</CardTitle>
          <CardDescription>
            Visualiza el inventario actual y el historial de movimientos
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <div className="flex items-center justify-between mb-4">
              <TabsList className="grid grid-cols-3">
                <TabsTrigger value="inventario" className="flex items-center gap-2">
                  <Package className="h-4 w-4" />
                  Inventario Actual
                </TabsTrigger>
                <TabsTrigger value="movimientos" className="flex items-center gap-2">
                  <FileText className="h-4 w-4" />
                  Movimientos
                </TabsTrigger>
                <TabsTrigger value="historial" className="flex items-center gap-2">
                  <History className="h-4 w-4" />
                  Historial Detallado
                </TabsTrigger>
              </TabsList>

              {/* Botones de Exportar y Filtrar - Solo para tabs inventario y movimientos */}
              {activeTab !== 'historial' && (
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setIsFilterDrawerOpen(true)}
                    className="flex items-center gap-2"
                  >
                    <FilterIcon className="h-4 w-4" />
                    Filtrar
                  </Button>
                  <ExportExcelButton
                    service={activeTab === 'inventario' ? exportInventarioService : exportMovimientosService}
                    filters={filters}
                    columns={activeTab === 'inventario' ? exportColumnsInventario : exportColumnsMovimientos}
                    fileName={`inventario_insumos_${activeTab}_${new Date().toISOString().split('T')[0]}`}
                  />
                </div>
              )}
            </div>

            {/* Tab Inventario Actual */}
            <TabsContent value="inventario" className="space-y-4">
              {/* Filtros Aplicados */}
              <AppliedFilters
                filters={filters}
                fields={filterFieldsInventario}
                onRemoveFilter={handleRemoveFilter}
              />
              
              <DataTable
                columns={columnsInventario}
                data={inventario}
                isLoading={isLoadingInventario}
              />
              <Pagination
                currentPage={currentPageInventario}
                totalItems={totalItemsInventario}
                itemsPerPage={ITEMS_PER_PAGE}
                onPageChange={setCurrentPageInventario}
              />
            </TabsContent>

            {/* Tab Movimientos */}
            <TabsContent value="movimientos" className="space-y-4">
              {/* Filtros Aplicados */}
              <AppliedFilters
                filters={filters}
                fields={filterFieldsMovimientos}
                onRemoveFilter={handleRemoveFilter}
              />
              
              <DataTable
                columns={columnsMovimientos}
                data={movimientos}
                isLoading={isLoadingMovimientos}
              />
              <Pagination
                currentPage={currentPageMovimientos}
                totalItems={totalItemsMovimientos}
                itemsPerPage={ITEMS_PER_PAGE}
                onPageChange={setCurrentPageMovimientos}
              />
            </TabsContent>

            {/* Tab Historial Detallado - Matriz */}
            <TabsContent value="historial" className="space-y-4">
              {/* Filtros Aplicados */}
              <AppliedFilters
                filters={filters}
                fields={filterFieldsHistorial}
                onRemoveFilter={handleRemoveFilter}
              />
              
              <div className="flex items-center justify-between gap-4">
                <div className="text-sm text-gray-600">
                  Matriz de entregas: Insumos (filas) × Entregas (columnas)
                </div>
                <div className="flex items-center gap-2">
                  <ExportExcelButton
                    service={async () => {
                      const { supplies, events } = buildStockMatrix();
                      return supplies.map(supply => {
                        const row = { 
                          insumo: supply.nombre, 
                          tipo: supply.tipo,
                          'Inventario Actual': supply.inventarioActual || 0
                        };
                        let totalEntregado = 0;
                        
                        events.forEach((event) => {
                          const itemData = event.itemsMap.get(supply.id);
                          const cantidad = itemData?.cantidad || 0;
                          // Incluir destino (edificio + hospedaje) en el nombre de la columna
                          const columnName = `${event.consecutivo.substring(0, 10)} (${event.destino})`;
                          row[columnName] = cantidad || '';
                          
                          if (cantidad > 0) totalEntregado += cantidad;
                        });
                        
                        row['Total Entregado'] = totalEntregado;
                        row['Inventario Inicial'] = (supply.inventarioActual || 0) + totalEntregado;
                        return row;
                      });
                    }}
                    filters={{}}
                    columns={[]}
                    fileName={`historial-detallado-insumos-${new Date().toISOString().split('T')[0]}`}
                  />
                  <Button
                    variant="outline"
                    onClick={() => setIsFilterDrawerOpen(true)}
                    className="flex items-center gap-2"
                  >
                    <FilterIcon className="h-4 w-4" />
                    Filtros
                  </Button>
                </div>
              </div>

              {isLoadingHistorial ? (
                <div className="flex items-center justify-center py-12">
                  <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto mb-4"></div>
                    <p className="text-gray-500">Cargando historial...</p>
                  </div>
                </div>
              ) : historialDetallado.length === 0 ? (
                <Card>
                  <CardContent className="py-12 text-center">
                    <History className="h-16 w-16 text-gray-300 mx-auto mb-4" />
                    <p className="text-gray-500">No hay registros en el historial</p>
                  </CardContent>
                </Card>
              ) : (() => {
                const { supplies, events } = buildStockMatrix();
                
                return (
                  <Card>
                    <CardContent className="p-0">
                      <div className="overflow-x-auto">
                        <table className="w-full border-collapse">
                          <thead className="sticky top-0 bg-gray-50 z-10">
                            <tr>
                              <th className="sticky left-0 bg-gray-100 border border-gray-300 p-3 text-left font-semibold text-sm min-w-[200px] z-20">
                                <div className="flex items-center gap-2">
                                  <Package className="h-4 w-4 text-green-600" />
                                  Insumo
                                </div>
                              </th>
                              {events.map((event) => (
                                <th 
                                  key={event.id}
                                  className="border border-gray-300 p-2 text-center font-semibold text-xs min-w-[120px] bg-orange-50"
                                >
                                  <div className="flex flex-col items-center gap-1">
                                    <div className="flex items-center gap-1">
                                      <ArrowDown className="h-3 w-3 text-orange-600" />
                                      <span className="text-orange-700">ENT</span>
                                    </div>
                                    <div className="text-xs font-mono text-gray-600">
                                      {event.consecutivo.substring(0, 15)}
                                    </div>
                                    <div className="text-xs text-gray-500">
                                      {event.fecha.toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit' })}
                                    </div>
                                    <div className="text-xs text-gray-600 truncate max-w-[120px]" title={event.destino}>
                                      {event.destino}
                                    </div>
                                  </div>
                                </th>
                              ))}
                              <th className="sticky right-[200px] bg-blue-100 border border-gray-300 p-2 text-center font-semibold text-xs min-w-[100px] z-20">
                                <div className="text-blue-700">Inventario<br/>Actual</div>
                              </th>
                              <th className="sticky right-[100px] bg-orange-100 border border-gray-300 p-2 text-center font-semibold text-xs min-w-[100px] z-20">
                                <div className="text-orange-700">Total<br/>Entregado</div>
                              </th>
                              <th className="sticky right-0 bg-green-100 border border-gray-300 p-2 text-center font-semibold text-xs min-w-[100px] z-20">
                                <div className="text-green-700">Inventario<br/>Inicial</div>
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            {supplies.map((supply, sIdx) => {
                              let totalEntregado = 0;
                              
                              return (
                                <tr key={supply.id} className={sIdx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                                  <td className="sticky left-0 border border-gray-300 p-3 font-medium text-sm z-10 bg-inherit">
                                    <div className="flex items-center gap-2">
                                      <Package className="h-3 w-3 text-green-600 flex-shrink-0" />
                                      <span className="truncate">{supply.nombre}</span>
                                    </div>
                                    <div className="text-xs text-gray-500 mt-1">
                                      {supply.tipo}
                                    </div>
                                  </td>
                                  {events.map((event) => {
                                    const itemData = event.itemsMap.get(supply.id);
                                    const cantidad = itemData?.cantidad || 0;
                                    
                                    if (cantidad > 0) totalEntregado += cantidad;
                                    
                                    return (
                                      <td 
                                        key={event.id}
                                        className="border border-gray-300 p-2 text-center"
                                      >
                                        {cantidad > 0 ? (
                                          <div className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-semibold bg-orange-100 text-orange-700">
                                            {cantidad}
                                          </div>
                                        ) : (
                                          <span className="text-gray-300 text-xs">—</span>
                                        )}
                                      </td>
                                    );
                                  })}
                                  <td className="sticky right-[200px] bg-blue-50 border border-gray-300 p-2 text-center font-bold text-sm z-10">
                                    <div className="text-blue-700">
                                      {supply.inventarioActual}
                                    </div>
                                  </td>
                                  <td className="sticky right-[100px] bg-orange-50 border border-gray-300 p-2 text-center font-bold text-sm z-10">
                                    <div className="text-orange-700">
                                      {totalEntregado}
                                    </div>
                                  </td>
                                  <td className="sticky right-0 bg-green-50 border border-gray-300 p-2 text-center font-bold text-sm z-10">
                                    <div className="text-green-700">
                                      {supply.inventarioActual + totalEntregado}
                                    </div>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                          <tfoot className="sticky bottom-0 bg-gray-100">
                            <tr>
                              <td className="sticky left-0 border border-gray-300 p-3 font-bold text-sm bg-gray-100 z-20">
                                Total por Entrega
                              </td>
                              {events.map((event) => {
                                const total = Array.from(event.itemsMap.values()).reduce(
                                  (sum, item) => sum + item.cantidad, 
                                  0
                                );
                                
                                return (
                                  <td 
                                    key={event.id}
                                    className="border border-gray-300 p-2 text-center font-bold text-sm bg-orange-50"
                                  >
                                    <div className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-bold bg-orange-200 text-orange-800">
                                      {total}
                                    </div>
                                  </td>
                                );
                              })}
                              <td className="sticky right-[200px] bg-blue-100 border border-gray-300 p-2 text-center font-bold text-sm z-20">
                                <div className="text-blue-800">
                                  {supplies.reduce((sum, s) => sum + (s.inventarioActual || 0), 0)}
                                </div>
                              </td>
                              <td className="sticky right-[100px] bg-orange-100 border border-gray-300 p-2 text-center font-bold text-sm z-20">
                                <div className="text-orange-800">
                                  {supplies.reduce((sum, s) => {
                                    let total = 0;
                                    events.forEach(e => {
                                      const cant = e.itemsMap.get(s.id)?.cantidad || 0;
                                      if (cant > 0) total += cant;
                                    });
                                    return sum + total;
                                  }, 0)}
                                </div>
                              </td>
                              <td className="sticky right-0 bg-green-100 border border-gray-300 p-2 text-center font-bold text-sm z-20">
                                <div className="text-green-800">
                                  {supplies.reduce((sum, s) => {
                                    let totalEntregado = 0;
                                    events.forEach(e => {
                                      const cant = e.itemsMap.get(s.id)?.cantidad || 0;
                                      if (cant > 0) totalEntregado += cant;
                                    });
                                    return sum + (s.inventarioActual || 0) + totalEntregado;
                                  }, 0)}
                                </div>
                              </td>
                            </tr>
                          </tfoot>
                        </table>
                      </div>
                      
                      {/* Leyenda */}
                      <div className="p-4 bg-gray-50 border-t border-gray-300">
                        <div className="flex items-center justify-center gap-6 text-xs flex-wrap">
                          <div className="flex items-center gap-2">
                            <div className="w-3 h-3 rounded-full bg-orange-100 border border-orange-300"></div>
                            <span className="text-gray-600">Entrega - Cantidad entregada</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <div className="w-3 h-3 rounded-full bg-blue-100 border border-blue-300"></div>
                            <span className="text-gray-600">Inventario Actual - Stock disponible ahora</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <div className="w-3 h-3 rounded-full bg-orange-100 border border-orange-300"></div>
                            <span className="text-gray-600">Total Entregado - Suma de entregas</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <div className="w-3 h-3 rounded-full bg-green-100 border border-green-300"></div>
                            <span className="text-gray-600">Inventario Inicial - Stock antes de entregas</span>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })()}
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>

      {/* Modal de Registrar Entrada */}
      <RegistrarEntradaInsumosModal
        isOpen={isEntradaModalOpen}
        onClose={() => setIsEntradaModalOpen(false)}
        onSuccess={handleEntradaSuccess}
        user={user}
      />

      {/* Modal de Ajuste de Inventario */}
      <AjustarInsumoModal
        isOpen={isAjusteModalOpen}
        onClose={() => {
          setIsAjusteModalOpen(false);
          setSelectedInsumoAjuste(null);
        }}
        onSuccess={handleAjusteSuccess}
        insumo={selectedInsumoAjuste}
        user={user}
      />

      {/* Modal de Selección de Insumo para Ajuste */}
      <SeleccionarInsumoAjusteModal
        isOpen={isAjusteGeneralModalOpen}
        onClose={() => setIsAjusteGeneralModalOpen(false)}
        onSelectInsumo={handleSelectInsumoAjuste}
      />

      {/* Filter Drawer */}
      <FilterDrawer
        isOpen={isFilterDrawerOpen}
        onClose={() => setIsFilterDrawerOpen(false)}
        fields={
          activeTab === 'inventario' 
            ? filterFieldsInventario 
            : activeTab === 'movimientos'
            ? filterFieldsMovimientos
            : filterFieldsHistorial
        }
        onApplyFilters={handleApplyFilters}
        initialFilters={filters}
      />
    </motion.div>
  );
};

export default InventarioInsumosPage;
